import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,

  MessageSquare,
} from 'lucide-react';
import { Mark } from '@/components/ui/Badge';
import { PageLayout } from '@/components/ui/PageLayout';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  The school-management modules that are scoped but not built.

  They sit in the navigation deliberately. A walkthrough that shows only what
  exists reads as a gate-security tool, and what is being sold is a school
  management platform — so the shape of the whole product has to be visible
  before the parts of it that work are demonstrated.

  Every one of these screens says plainly that it is not built. The alternative
  — a page that looks operational and is quietly empty — invites someone to
  click into it during a demo and discover that for themselves.

  One component and one spec list rather than seven near-identical page files,
  for the same reason the navigation is data: seven files is seven places to
  edit when the scope of any one of them moves.
*/

export interface ModuleSpec {
  title: string;
  /** One line saying what the screen is, in the words a school office uses. */
  subtitle: string;
  /** The nav group it sits under, so the breadcrumb matches the sidebar. */
  group: string;
  icon: LucideIcon;
  /** What the module covers. Four lines — enough to show the shape of it,
   *  short enough to read from the back of a room. */
  covers: string[];
}

export const MODULES: Record<string, ModuleSpec> = {
  '/homework': {
    title: 'Homework',
    subtitle: 'Set work and track what has been handed in',
    group: 'Academics',
    icon: BookOpen,
    covers: [
      'Create assignments for a class or a group, with a due date',
      'Attach worksheets and reading for parents to download',
      'See submission and completion rates per student',
      'Push overdue work to the parent app automatically',
    ],
  },
  '/messages': {
    title: 'Messages',
    subtitle: 'Direct conversation between the office and a parent',
    group: 'Communication',
    icon: MessageSquare,
    covers: [
      'Message one parent or open a whole-class thread',
      'Keep every exchange against the student record',
      'Route replies to the staff member who owns the thread',
      'Hand a conversation over without losing its history',
    ],
  },
};

export function ModulePreviewPage({ module }: { module: ModuleSpec }) {
  const { title, subtitle, group, icon: Icon, covers } = module;

  return (
    <PageLayout
      title={title}
      subtitle={subtitle}
      breadcrumbs={[{ label: group }, { label: title }]}
      meta={<Mark tone="info">On the roadmap</Mark>}
      contained={false}
    >
      <div className={cn(styles.panel, 'max-w-3xl')}>
        <div className="flex items-start gap-3 px-4 py-4">
          {/* The module mark, built like every other icon frame in the product:
              a 4px-radius box on the muted ground, never a floating circle. */}
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
            <Icon size={18} strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <p className={styles.sectionTitle}>Not built yet</p>
            <p className={cn(styles.meta, 'mt-1 max-w-xl')}>
              {title} is scoped but not implemented. It is shown here so the full shape of the
              platform is visible — the screens that are live are Students, Groups, Attendance,
              Fees, Events, the notice board, Staff, the audit log and Reports.
            </p>
          </div>
        </div>

        <div className={styles.panelRow}>
          <p className={cn(styles.label, 'mb-2')}>What it will cover</p>
          <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {covers.map((line) => (
              <li key={line} className="flex items-start gap-2">
                {/* A rule, not a tick. A tick says done, and none of this is. */}
                <span
                  className="mt-[7px] h-[2px] w-2 flex-shrink-0 rounded-full bg-muted-foreground/50"
                  aria-hidden
                />
                <span className="text-[11.5px] leading-relaxed text-foreground">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </PageLayout>
  );
}
